import {
	Component,
	computed,
	ElementRef,
	inject,
	input,
	signal,
	viewChildren,
} from '@angular/core';
import { Breadcrumb } from '@agilox/ui-common';
import { NgTemplateOutlet } from '@angular/common';
import { RouterLink } from '@angular/router';
import { IconModule } from '../icon/icon.module';
import { BreadcrumbEllipsisPipe } from './pipes/breadcrumb-ellipsis.pipe';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
	selector: 'ui-breadcrumb',
	imports: [IconModule, BreadcrumbEllipsisPipe, NgTemplateOutlet, RouterLink],
	templateUrl: './breadcrumbs.component.html',
})
export class BreadcrumbsComponent {
	private breakpointObserver = inject(BreakpointObserver);

	breadcrumbItems = viewChildren<ElementRef<HTMLElement>>('breadcrumbItem');

	icon = input<string>('');

	breadcrumbs = input<Breadcrumb[]>([]);

	tabIndex = input<number>(0);

	/**
	 * The amount of breadcrumbs to display
	 *
	 * if the amount of breadcrumbs is higher than the maxBreadcrumbs
	 * only the following breadcrumbs will be displayed:
	 * 1. first breadcrumb
	 * 2. breadcrumbs in between the range of maxBreadcrumbs to length of the array of breadcrumbs
	 *
	 * @default 4
	 * @min 1
	 * @example maxBreadcrumbs = 4
	 * breadcrumbs = [1,2,3,4,5,6,7,8,9,10]
	 * following breadcrumbs will be displayed:
	 * 1, ... , 8, 9, 10
	 */
	maxBreadcrumbs = input(4, {
		transform: (value: number) => (value < 1 ? 1 : value),
	});

	multiline = input<boolean>(false);

	private isMobile = toSignal(
		this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small])
	);

	public effectiveMaxBreadcrumbs = computed(() => {
		if (this.multiline()) {
			return this.breadcrumbs().length;
		}
		// On mobile, always show only 1 (last item)
		if (this.isMobile()?.matches) {
			return 1;
		}
		// On desktop, use the input value
		return this.maxBreadcrumbs();
	});

	private userExpanded = signal(false);

	/**
	 * Only in use when the amount of breadcrumbs is higher than the maxBreadcrumbs
	 * Automatically set to false on mobile devices
	 */
	expanded = computed(() => {
		// On mobile, always collapsed
		if (this.isMobile()?.matches && !this.multiline()) {
			return false;
		}
		// On desktop, use user preference
		return this.userExpanded();
	});

	toggleExpanded(): void {
		this.userExpanded.set(!this.userExpanded());
	}

	handleKeyDown(event: KeyboardEvent) {
		const items = this.breadcrumbItems();
		const index = items.findIndex((item) => item.nativeElement === event.currentTarget);

		if (index === -1) {
			return;
		}

		switch (event.key) {
			case 'ArrowLeft':
				event.preventDefault();
				this.focusItem(index > 0 ? index - 1 : items.length - 1);
				break;

			case 'ArrowRight':
				event.preventDefault();
				this.focusItem(index < items.length - 1 ? index + 1 : 0);
				break;

			case 'Home':
				event.preventDefault();
				this.focusItem(0);
				break;

			case 'End':
				event.preventDefault();
				this.focusItem(items.length - 1);
				break;
		}
	}

	private focusItem(index: number) {
		this.breadcrumbItems()[index]?.nativeElement?.focus();
	}
}
